import {HoistModel, XH} from '@xh/hoist/core';
import {action, computed, observable, observableRef} from '@xh/hoist/mobx';
import {SelectOption} from '../../../cmp/gridSpec/configurator/GridSpecConfiguratorModel';
import {castArray, isArray, isEmpty, isNil} from 'lodash';
import {ResearchDataFilter, ResearchDataset, ResearchDataSource, ResearchFilterOp, ResearchWidgetKind} from '../../../data/ResearchTypes';
import {fieldLabel, TICKER_FIELD} from '../ResearchDatasets';

/** The ways a filter compares a field - all of them, as a source may come from the AI assistant. */
export const FILTER_OP_OPTIONS: {value: ResearchFilterOp; label: string}[] = [
    {value: 'eq', label: '='},
    {value: 'ne', label: '≠'},
    {value: 'gt', label: '>'},
    {value: 'gte', label: '≥'},
    {value: 'lt', label: '<'},
    {value: 'lte', label: '≤'},
    {value: 'in', label: 'any of'},
    {value: 'contains', label: 'contains'},
    {value: 'isNull', label: 'is empty'},
    {value: 'isNotNull', label: 'has a value'}
];

/** True for the ops that compare with nothing. */
export function isValueless(op: ResearchFilterOp): boolean {
    return op === 'isNull' || op === 'isNotNull';
}

/** Edits a draft of a widget's data source - its dataset and how the rows are narrowed. */
export class ResearchDataDialogModel extends HoistModel {
    @observable accessor isOpen = false;
    @observableRef accessor draft: ResearchDataSource = null;

    kind: ResearchWidgetKind;

    private readonly onSave: (source: ResearchDataSource) => void;

    constructor(onSave: (source: ResearchDataSource) => void) {
        super();
        this.onSave = onSave;
    }

    @computed get dataset(): ResearchDataset {
        return XH.researchService.getDataset(this.draft?.dataset);
    }

    /** Datasets by category, for a grouped select. */
    get datasetOptions(): {label: string; options: SelectOption[]}[] {
        const groups = new Map<string, SelectOption[]>();
        XH.researchService.datasets.forEach(d => {
            if (!groups.has(d.category)) groups.set(d.category, []);
            groups.get(d.category).push({value: d.name, label: d.title});
        });
        return [...groups.entries()].map(([label, options]) => ({label, options}));
    }

    /**
     * Fields rows can be narrowed by - the ones that describe a row rather than measure something, and
     * any other the source already filters on.
     */
    @computed get filterFieldOptions(): SelectOption[] {
        const used = this.filters.map(f => f.field);
        return (this.dataset?.fields ?? [])
            .filter(f => f.name !== TICKER_FIELD && (used.includes(f.name) || (f.role !== 'measure' && f.role !== 'timestamp')))
            .map(f => ({value: f.name, label: fieldLabel(f.name)}));
    }

    get filters(): ResearchDataFilter[] {
        return this.draft?.filters ?? [];
    }

    get canSave(): boolean {
        const hasValue = (f: ResearchDataFilter) => !isNil(f.value) && f.value !== '' && !(isArray(f.value) && isEmpty(f.value));
        return !!this.dataset && this.filters.every(f => f.field && (isValueless(f.op) || hasValue(f)));
    }

    valueOptions(field: string): string[] {
        return this.dataset?.fields.find(f => f.name === field)?.enumValues ?? null;
    }

    @action
    open(kind: ResearchWidgetKind, source: ResearchDataSource) {
        this.kind = kind;
        this.draft = source ? {...source} : {dataset: null};
        this.isOpen = true;
    }

    @action
    close() {
        this.isOpen = false;
    }

    /** Another dataset has other fields, so the narrowing starts over. */
    @action
    setDataset(dataset: string) {
        this.draft = {dataset};
    }

    @action
    update(changes: Partial<ResearchDataSource>) {
        this.draft = {...this.draft, ...changes};
    }

    addFilter() {
        this.update({filters: [...this.filters, {field: this.filterFieldOptions[0]?.value ?? null, op: 'eq', value: null}]});
    }

    updateFilter(idx: number, changes: Partial<ResearchDataFilter>) {
        // A value picked for one field means nothing for another
        if ('field' in changes) changes = {...changes, value: null};
        // 'any of' compares with a list, the others with one value or with none
        if ('op' in changes) {
            const first = castArray(this.filters[idx].value ?? [])[0] ?? null;
            changes = {...changes, value: isValueless(changes.op) ? undefined : changes.op === 'in' ? (isNil(first) ? [] : [first]) : first};
        }
        this.update({filters: this.filters.map((f, i) => (i === idx ? {...f, ...changes} : f))});
    }

    removeFilter(idx: number) {
        this.update({filters: this.filters.filter((_, i) => i !== idx)});
    }

    save() {
        const {dataset, latest, transpose, fields} = this.draft,
            source: ResearchDataSource = {dataset};

        if (latest && this.dataset.supportsLatest) source.latest = true;
        if (transpose && this.kind === 'grid') source.transpose = true;
        if (this.filters.length) source.filters = this.filters;
        // The lines chosen for a one-line-per-field grid stay while it shows the same dataset
        if (source.transpose && !isEmpty(fields)) source.fields = fields;

        this.onSave(source);
        this.close();
    }
}
